/* eslint-disable max-lines */
export const AiEngineLabels = {
  title: 'AI Engine Console',
  subtitle: 'Agents, skills, models & platform health',
  tagline: 'Grounded by Deloitte Trustworthy AI™',
  deployAgent: '+ Deploy Agent',
  addSkill: '+ Add Skill',
};

export const AiEngineTabKeys = {
  SUMMARY: 'Summary',
  AGENTS: 'Agent Registry',
  SKILLS: 'Skills Registry',
  TOOLKITS: 'Toolkit Registry',
  MODELS: 'Model Registry',
  LLM_HEALTH: 'LLM Health',
  LLM_SETTINGS: 'LLM Settings',
  TAI_GUARDRAILS: 'TAI Guardrails',
};

export type TaiMode = 'Enforced' | 'Monitor Only' | 'Advisory';

export interface ITaiPillar {
  id: string;
  name: string;
  color: string;
  tags: string[];
  defaultEnabled: boolean;
  defaultMode: TaiMode;
}

export const TAI_PILLARS: ITaiPillar[] = [
  {
    id: 'safe-secure',
    name: 'Safe & Secure',
    color: '#0f172a',
    tags: ['Invulnerable', 'User Protection', 'User Friendly'],
    defaultEnabled: true,
    defaultMode: 'Enforced',
  },
  {
    id: 'private',
    name: 'Private',
    color: '#1d4ed8',
    tags: ['Autonomous', 'Confidential', 'Discretional', 'Consensual'],
    defaultEnabled: true,
    defaultMode: 'Enforced',
  },
  {
    id: 'transparent-explainable',
    name: 'Transparent & Explainable',
    color: '#0d9488',
    tags: ['Justifiable', 'Interpretable', 'Auditable', 'Visible'],
    defaultEnabled: true,
    defaultMode: 'Enforced',
  },
  {
    id: 'fair-impartial',
    name: 'Fair & Impartial',
    color: '#16a34a',
    tags: ['Accessible', 'Non-discriminatory', 'Inclusive', 'Unbiased'],
    defaultEnabled: true,
    defaultMode: 'Enforced',
  },
  {
    id: 'responsible',
    name: 'Responsible',
    color: '#65a30d',
    tags: ['Humane', 'Common & Social Good', 'Sustainability Focused', 'Value Adding', 'People-led'],
    defaultEnabled: true,
    defaultMode: 'Enforced',
  },
  {
    id: 'accountable',
    name: 'Accountable',
    color: '#84cc16',
    tags: ['Answerable', 'Resolvable', 'Ownership'],
    defaultEnabled: true,
    defaultMode: 'Enforced',
  },
  {
    id: 'robust-reliable',
    name: 'Robust & Reliable',
    color: '#064e3b',
    tags: ['Predictable', 'Consistent', 'Accurate', 'Adaptable'],
    defaultEnabled: true,
    defaultMode: 'Enforced',
  },
];

export const AI_ENGINE_KPIS = [
  { label: 'Active Agents', value: '8', desc: 'Across all offerings', color: '#6AAF1F' },
  { label: 'Skills Deployed', value: '44', desc: 'Platform-wide', color: '#00B5AD' },
  { label: 'Models in Use', value: '4', desc: 'Opus · Sonnet · Haiku · Custom', color: '#8844CC' },
  { label: 'Avg Latency', value: '0.9s', desc: 'Cross-model P50', color: '#1E5FA8' },
];

/* ─────────────── AGENTS ─────────────── */
export interface IAgent {
  name: string;
  model: string;
  status: 'Active' | 'Running' | 'Idle';
  tasks: number;
  color: string;
  offering: string;
  layer: 'aicode' | 'rag' | 'orchestration' | 'routing';
  desc: string;
}

export const AI_AGENTS: IAgent[] = [
  { name: 'Orchestrator', model: 'Claude Opus', status: 'Active', tasks: 105, color: '#6AAF1F', offering: 'Platform-wide', layer: 'orchestration', desc: 'Coordinates all inter-agent handoffs, context sharing, and dependency resolution.' },
  { name: 'Migration Agent', model: 'Claude Opus', status: 'Running', tasks: 186, color: '#1A8C7A', offering: 'Cloud Enablement', layer: 'aicode', desc: 'Generates and executes IaC for live migration waves, handles rollback orchestration.' },
  { name: 'Foundation Agent', model: 'Claude Sonnet', status: 'Active', tasks: 38, color: '#4C9E1A', offering: 'Cloud Enablement', layer: 'aicode', desc: 'Provisions landing zones, IAM, security baselines, and network architecture as code.' },
  { name: 'Strategy Agent', model: 'Claude Sonnet', status: 'Active', tasks: 24, color: '#6AAF1F', offering: 'Cloud Enablement', layer: 'rag', desc: 'Retrieves DCA frameworks, benchmarks, and patterns for strategy and assessment outputs.' },
  { name: 'SRE Agent', model: 'Claude Sonnet', status: 'Active', tasks: 61, color: '#006B8C', offering: 'Cloud Enablement', layer: 'orchestration', desc: 'Manages observability stacks, FinOps analysis, incident triage, and compliance monitoring.' },
  { name: 'Clinical Safety Agent', model: 'Claude Sonnet', status: 'Active', tasks: 94, color: '#00B5AD', offering: 'Industry Solutions', layer: 'rag', desc: 'Monitors pharmacovigilance data, surfaces adverse event signals, assembles evidence chains.' },
  { name: 'Regulatory AI Agent', model: 'Claude Haiku', status: 'Idle', tasks: 0, color: '#1E5FA8', offering: 'Industry Solutions', layer: 'routing', desc: 'Maps regulation changes to internal controls and generates remediation plans.' },
  { name: 'AIOps Agent', model: 'Claude Sonnet', status: 'Running', tasks: 429, color: '#8844CC', offering: 'Industry Solutions', layer: 'orchestration', desc: 'Triages and correlates IT incidents, executes auto-remediation, generates post-incident reports.' },
];

export const AGENT_OFFERINGS: { name: string; color: string }[] = [
  { name: 'Platform-wide', color: '#6AAF1F' },
  { name: 'Cloud Enablement', color: '#4C9E1A' },
  { name: 'Industry Solutions', color: '#1E5FA8' },
];

/* ─────────────── SKILLS ─────────────── */
export interface ISkill {
  id: string;
  title: string;
  category: string;
  desc: string;
  maturity: 'Production' | 'Beta' | 'Pilot';
  usedBy: number;
  color: string;
  // When set, the "View Details" button fetches the live SKILL.md body
  // from the DCA AI Skills Engine: GET /api/skills/{engineSkill}.
  engineSkill?: string;
}

export const SKILL_GROUPS: { cat: string; label: string; color: string }[] = [
  { cat: 'cloud', label: 'Cloud Engineering', color: '#0288D1' },
  { cat: 'devops', label: 'DevOps & Platform', color: '#4527A0' },
  { cat: 'appdev', label: 'App Development', color: '#2E7D32' },
  { cat: 'banking', label: 'Banking & Finance', color: '#1565C0' },
  { cat: 'lifesciences', label: 'Life Sciences', color: '#388E3C' },
  { cat: 'it', label: 'Technology & Ops', color: '#6A1B9A' },
  { cat: 'regulatory', label: 'Regulatory', color: '#00695C' },
  { cat: 'crossindustry', label: 'Cross-Industry', color: '#4E342E' },
];

export const AI_SKILLS: ISkill[] = [
  /* Cloud Engineering (7) */
  { id: 'aws-cloud-eng', title: 'AWS Cloud Engineer', category: 'cloud', desc: 'End-to-end AWS resource provisioning and best-practice configuration.', maturity: 'Production', usedBy: 1, color: '#FF9900', engineSkill: 'aws-cloud-engineer' },
  { id: 'lz-provisioner', title: 'Landing Zone Provisioner', category: 'cloud', desc: 'Designs multi-account landing zones aligned to AWS/Azure/GCP guardrails.', maturity: 'Production', usedBy: 38, color: '#0288D1' },
  { id: 'cloud-cost-opt', title: 'Cloud Cost Optimiser', category: 'cloud', desc: 'Identifies idle resources, rightsizes workloads, and projects savings.', maturity: 'Production', usedBy: 52, color: '#0288D1' },
  { id: 'cloud-sec-cspa', title: 'Cloud Security Posture Advisor', category: 'cloud', desc: 'Continuous CSPM scanning with prioritised remediation plans.', maturity: 'Production', usedBy: 29, color: '#0288D1' },
  { id: 'migration-wave', title: 'Migration Wave Planner', category: 'cloud', desc: 'Plans migration waves, dependencies and rollback windows.', maturity: 'Production', usedBy: 21, color: '#0288D1' },
  { id: 'well-arch', title: 'Well-Architected Reviewer', category: 'cloud', desc: 'Reviews workloads against Well-Architected Framework pillars.', maturity: 'Beta', usedBy: 14, color: '#0288D1' },
  { id: 'resource-tag', title: 'Resource Tagging Enforcer', category: 'cloud', desc: 'Enforces FinOps and governance tagging standards across accounts.', maturity: 'Production', usedBy: 61, color: '#0288D1' },

  /* DevOps & Platform (8) */
  { id: 'terraform-dev', title: 'Terraform Developer', category: 'devops', desc: 'Generates production-ready Terraform modules from natural language.', maturity: 'Production', usedBy: 1, color: '#4527A0', engineSkill: 'terraform-developer' },
  { id: 'cicd-gen', title: 'CI/CD Pipeline Generator', category: 'devops', desc: 'Generates GitHub Actions, GitLab CI, and Azure DevOps pipelines.', maturity: 'Production', usedBy: 74, color: '#4527A0' },
  { id: 'sre-runbook', title: 'SRE Runbook Generator', category: 'devops', desc: 'Generates incident runbooks from system topology and past incidents.', maturity: 'Production', usedBy: 33, color: '#4527A0' },
  { id: 'obs-stack', title: 'Observability Stack Configurator', category: 'devops', desc: 'Configures OTel, Datadog, Prometheus + Grafana stacks.', maturity: 'Production', usedBy: 41, color: '#4527A0' },
  { id: 'container-opt', title: 'Container Image Optimiser', category: 'devops', desc: 'Optimises container images for size, security and start-up time.', maturity: 'Production', usedBy: 58, color: '#4527A0' },
  { id: 'gitops-flow', title: 'GitOps Workflow Designer', category: 'devops', desc: 'Designs ArgoCD/Flux GitOps workflows with promotion gates.', maturity: 'Beta', usedBy: 19, color: '#4527A0' },
  { id: 'release-notes', title: 'Release Notes Composer', category: 'devops', desc: 'Generates customer-facing release notes from PR history.', maturity: 'Production', usedBy: 87, color: '#4527A0' },
  { id: 'chaos-eng', title: 'Chaos Engineering Scenario Builder', category: 'devops', desc: 'Designs and executes safe chaos experiments to validate resilience.', maturity: 'Beta', usedBy: 8, color: '#C62828' },

  /* App Development (7) */
  { id: 'api-design', title: 'API Design Assistant', category: 'appdev', desc: 'Generates OpenAPI specs with examples, error schemas and auth.', maturity: 'Production', usedBy: 96, color: '#2E7D32' },
  { id: 'unit-test', title: 'Unit Test Generator', category: 'appdev', desc: 'Generates high-coverage unit tests from source code.', maturity: 'Production', usedBy: 142, color: '#2E7D32' },
  { id: 'schema-designer', title: 'Database Schema Designer', category: 'appdev', desc: 'Designs normalised schemas with indexes and migration scripts.', maturity: 'Production', usedBy: 64, color: '#2E7D32' },
  { id: 'a11y-checker', title: 'Accessibility Compliance Checker', category: 'appdev', desc: 'Validates UI against WCAG 2.2 AA and suggests fixes.', maturity: 'Beta', usedBy: 27, color: '#8844CC' },
  { id: 'code-review', title: 'Code Review Companion', category: 'appdev', desc: 'Reviews PRs for bugs, perf and security; suggests inline diffs.', maturity: 'Production', usedBy: 211, color: '#2E7D32' },
  { id: 'refactor-coach', title: 'Refactor Coach', category: 'appdev', desc: 'Suggests safe refactors with test coverage validation.', maturity: 'Production', usedBy: 53, color: '#2E7D32' },
  { id: 'doc-writer', title: 'Documentation Writer', category: 'appdev', desc: 'Generates API docs, READMEs and architecture diagrams.', maturity: 'Production', usedBy: 178, color: '#2E7D32' },

  /* Banking & Finance (4) */
  { id: 'credit-risk', title: 'Credit Risk Scoring', category: 'banking', desc: 'Explainable credit risk assessment using multi-factor ML models.', maturity: 'Production', usedBy: 14, color: '#1565C0' },
  { id: 'fraud-detect', title: 'Fraud Pattern Detection', category: 'banking', desc: 'Real-time anomaly detection on transaction streams with reasoning.', maturity: 'Production', usedBy: 9, color: '#1565C0' },
  { id: 'kyc-doc', title: 'KYC Document Analysis', category: 'banking', desc: 'Automated extraction and verification of identity documents.', maturity: 'Production', usedBy: 22, color: '#1565C0' },
  { id: 'aml-monitor', title: 'AML Transaction Monitor', category: 'banking', desc: 'Detects layering, structuring and high-risk counterparty patterns.', maturity: 'Beta', usedBy: 7, color: '#1565C0' },

  /* Life Sciences (4) */
  { id: 'ae-signal', title: 'Adverse Event Signal Detection', category: 'lifesciences', desc: 'Monitors PV databases and RWE for emerging safety signals.', maturity: 'Production', usedBy: 6, color: '#388E3C' },
  { id: 'narrative-writer', title: 'Clinical Narrative Writer', category: 'lifesciences', desc: 'Generates regulator-aligned narratives in ICH E3 and CIOMS formats.', maturity: 'Production', usedBy: 18, color: '#388E3C' },
  { id: 'protocol-author', title: 'Protocol Author Assistant', category: 'lifesciences', desc: 'Drafts clinical trial protocols aligned to ICH-GCP.', maturity: 'Beta', usedBy: 5, color: '#388E3C' },
  { id: 'submission-pack', title: 'Submission Pack Builder', category: 'lifesciences', desc: 'Assembles eCTD-ready regulatory submission packs.', maturity: 'Production', usedBy: 12, color: '#388E3C' },

  /* Technology & Ops (4) */
  { id: 'aiops-correlator', title: 'AIOps Incident Correlator', category: 'it', desc: 'Correlates alerts across tools and proposes remediation playbooks.', maturity: 'Beta', usedBy: 11, color: '#6A1B9A' },
  { id: 'capacity-plan', title: 'Capacity Planner', category: 'it', desc: 'Forecasts workload growth and recommends capacity changes.', maturity: 'Production', usedBy: 19, color: '#6A1B9A' },
  { id: 'itsm-resolver', title: 'ITSM Auto-Resolver', category: 'it', desc: 'Resolves common Tier-1 tickets with full audit trail.', maturity: 'Production', usedBy: 312, color: '#6A1B9A' },
  { id: 'change-impact', title: 'Change Impact Analyser', category: 'it', desc: 'Predicts blast radius of changes via topology graph.', maturity: 'Production', usedBy: 47, color: '#6A1B9A' },

  /* Regulatory (4) */
  { id: 'reg-mapper', title: 'Regulation Change Mapper', category: 'regulatory', desc: 'Maps regulatory changes to internal controls and procedures.', maturity: 'Production', usedBy: 22, color: '#00695C' },
  { id: 'audit-trail', title: 'Audit Trail Analyser', category: 'regulatory', desc: 'Reviews audit trails for anomalies and control failures.', maturity: 'Production', usedBy: 34, color: '#00695C' },
  { id: 'policy-q', title: 'Policy Q&A', category: 'regulatory', desc: 'Answers regulatory and policy questions with citations.', maturity: 'Production', usedBy: 89, color: '#00695C' },
  { id: 'compliance-mon', title: 'Compliance Monitor', category: 'regulatory', desc: 'Continuous compliance posture monitoring with daily delta reports.', maturity: 'Beta', usedBy: 16, color: '#00695C' },

  /* Cross-Industry (6) */
  { id: 'knowledge-qa', title: 'Enterprise Knowledge Q&A', category: 'crossindustry', desc: 'RAG-powered Q&A across wikis, runbooks and policies.', maturity: 'Production', usedBy: 112, color: '#8844CC' },
  { id: 'meeting-intel', title: 'Meeting Intelligence', category: 'crossindustry', desc: 'Transcribes, summarises and extracts actions from meetings.', maturity: 'Production', usedBy: 264, color: '#4E342E' },
  { id: 'translate', title: 'Document Translation', category: 'crossindustry', desc: 'Translates business documents across 70+ languages.', maturity: 'Production', usedBy: 88, color: '#4E342E' },
  { id: 'sentiment', title: 'Customer Sentiment Analyser', category: 'crossindustry', desc: 'Real-time sentiment scoring across support channels.', maturity: 'Production', usedBy: 41, color: '#4E342E' },
  { id: 'quals-builder', title: 'Quals Builder', category: 'crossindustry', desc: 'Assembles project qualifications from prior engagement history.', maturity: 'Beta', usedBy: 28, color: '#4E342E' },
  { id: 'rfp-assistant', title: 'RFP Response Assistant', category: 'crossindustry', desc: 'Drafts RFP responses grounded in win library and SME content.', maturity: 'Production', usedBy: 56, color: '#4E342E' },
];

/* ─────────────── TOOLKITS ─────────────── */
export interface IToolkit {
  id: string;
  category: 'orchestration' | 'rag' | 'devops' | 'observability' | 'compliance';
  name: string;
  vendor: string;
  type: string;
  status: 'Active' | 'Degraded';
  desc: string;
  capabilities: string[];
  usedBy: string[];
  version: string;
}

export const TOOLKIT_CATEGORIES: { id: string; label: string; color: string }[] = [
  { id: 'orchestration', label: 'Orchestration & Agent Infra', color: '#6AAF1F' },
  { id: 'rag', label: 'RAG & Knowledge', color: '#1A9E8F' },
  { id: 'devops', label: 'DevOps & IaC', color: '#4527A0' },
  { id: 'observability', label: 'Observability & ITSM', color: '#006B8C' },
  { id: 'compliance', label: 'Compliance & Regulatory', color: '#2E7D32' },
];

export const AI_TOOLKITS: IToolkit[] = [
  { id: 'handoff-bus', category: 'orchestration', name: 'Agent Handoff Bus', vendor: 'DCA Internal', type: 'Messaging', status: 'Active',
    desc: 'Pub/sub message bus that connects all platform agents for reliable, ordered inter-agent communication and context passing.',
    capabilities: ['Point-to-point messaging', 'Broadcast to agent groups', 'Dead-letter queue for failed messages', 'Message ordering guarantees'],
    usedBy: ['Orchestrator', 'Migration Agent', 'AIOps Agent'], version: 'v3.1.2' },
  { id: 'context-store', category: 'orchestration', name: 'Shared Context Store', vendor: 'DCA Internal', type: 'State Management', status: 'Active',
    desc: 'Distributed key-value store maintaining shared state and memory across multi-agent task chains, with TTL-based eviction.',
    capabilities: ['Cross-agent state sharing', 'TTL-based expiry', 'Namespace isolation per task', 'Audit log of all reads/writes'],
    usedBy: ['Orchestrator', 'Clinical Safety Agent', 'Strategy Agent'], version: 'v2.8.0' },
  { id: 'dep-resolver', category: 'orchestration', name: 'Dependency Resolver', vendor: 'DCA Internal', type: 'Scheduling', status: 'Active',
    desc: 'DAG-based task dependency graph and scheduler — ensures agents execute in the correct order and receive required inputs.',
    capabilities: ['DAG construction from task specs', 'Circular dependency detection', 'Parallel execution where possible', 'Retry with backoff'],
    usedBy: ['Orchestrator'], version: 'v1.9.4' },
  { id: 'escalation-router', category: 'orchestration', name: 'Escalation Router', vendor: 'DCA Internal', type: 'Workflow', status: 'Active',
    desc: 'Routes stalled or low-confidence tasks to the human review queue with full context bundle for informed decision making.',
    capabilities: ['Confidence threshold detection', 'Human review queue integration', 'Context bundle assembly', 'SLA monitoring'],
    usedBy: ['Orchestrator', 'AIOps Agent', 'Regulatory AI Agent'], version: 'v2.2.1' },
  { id: 'rag-engine', category: 'rag', name: 'DCA RAG Engine', vendor: 'DCA Internal', type: 'Retrieval', status: 'Active',
    desc: 'DCA proprietary RAG framework with advanced chunking, embedding generation, semantic search, and context assembly for grounded LLM outputs.',
    capabilities: ['Hybrid search (dense + sparse)', 'Multi-document synthesis', 'Source citation extraction', 'Confidence scoring'],
    usedBy: ['Strategy Agent', 'Clinical Safety Agent', 'Quals Builder', 'Enterprise Knowledge Q&A'], version: 'v4.0.1' },
  { id: 'perm-filter', category: 'rag', name: 'Permission Filter', vendor: 'DCA Internal', type: 'Security', status: 'Active',
    desc: 'Enforces document-level and field-level access controls before retrieval — ensuring agents only surface content users are authorised to see.',
    capabilities: ['Role-based document filtering', 'Field-level redaction', 'Audit trail per query', 'ABAC policy engine'],
    usedBy: ['Enterprise Knowledge Q&A', 'Foundation Agent'], version: 'v1.6.3' },
  { id: 'vector-db', category: 'rag', name: 'Vector Database', vendor: 'Pinecone', type: 'Storage', status: 'Active',
    desc: 'Managed vector database providing high-throughput semantic similarity search across 12K+ enterprise documents with sub-20ms retrieval.',
    capabilities: ['Billion-scale ANN search', 'Metadata filtering', 'Namespaced collections', 'Real-time upserts'],
    usedBy: ['DCA RAG Engine'], version: 'Serverless' },
  { id: 'kb-adapters', category: 'rag', name: 'Knowledge Base Adapters', vendor: 'DCA Internal', type: 'Connectors', status: 'Active',
    desc: 'Pre-built connectors for enterprise knowledge sources — ingesting, chunking, and indexing content into the RAG pipeline automatically.',
    capabilities: ['SharePoint / OneDrive', 'Confluence', 'AWS S3 / GCS', 'Notion', 'Custom REST APIs'],
    usedBy: ['DCA RAG Engine', 'Enterprise Knowledge Q&A'], version: 'v2.4.0' },
  { id: 'iac-runner', category: 'devops', name: 'IaC Execution Runner', vendor: 'DCA Internal', type: 'Execution', status: 'Active',
    desc: 'Safe execution environment for generated Terraform, CDK, and Pulumi code — includes dry-run, diff, security scan, and apply with rollback.',
    capabilities: ['Terraform plan / apply', 'CDK synth / deploy', 'Security scan before apply', 'Drift detection', 'Rollback on failure'],
    usedBy: ['Migration Agent', 'Foundation Agent'], version: 'v3.3.0' },
  { id: 'cicd-connector', category: 'devops', name: 'CI/CD Pipeline Connector', vendor: 'Multi', type: 'Integration', status: 'Active',
    desc: 'Bidirectional integration with GitHub Actions, GitLab CI, Azure DevOps, and Jenkins.',
    capabilities: ['Pipeline status polling', 'Trigger on agent completion', 'Log streaming', 'Artifact retrieval'],
    usedBy: ['Foundation Agent', 'Strategy Agent'], version: 'v1.8.2' },
  { id: 'container-scan', category: 'devops', name: 'Container Security Scanner', vendor: 'Trivy/Snyk', type: 'Security', status: 'Active',
    desc: 'Integrated container image scanning for CVEs, secrets, misconfigurations, and licence violations before deployment.',
    capabilities: ['CVE database (daily updates)', 'Secret detection', 'Dockerfile linting', 'SBOM generation'],
    usedBy: ['Foundation Agent', 'DevSecOps Pipeline Advisor'], version: 'v0.48.0' },
  { id: 'alert-correlator', category: 'observability', name: 'Alert Correlation Engine', vendor: 'DCA Internal', type: 'AIOps', status: 'Active',
    desc: 'Deduplicates and semantically groups related alerts from multiple sources into a single correlated incident within seconds.',
    capabilities: ['Multi-source ingestion (Datadog, PagerDuty, Splunk)', 'ML-based correlation', 'Noise reduction', 'Alert enrichment'],
    usedBy: ['AIOps Agent', 'SRE Agent'], version: 'v2.6.1' },
  { id: 'topology-graph', category: 'observability', name: 'Service Topology Graph', vendor: 'DCA Internal', type: 'Dependency Map', status: 'Active',
    desc: 'Real-time service dependency graph for blast radius analysis.',
    capabilities: ['Auto-discovery via OTel', 'Real-time updates', 'SLO impact scoring', 'K8s + VM + serverless'],
    usedBy: ['AIOps Agent', 'Incident Commander'], version: 'v1.4.0' },
  { id: 'playbook-exec', category: 'observability', name: 'Playbook Executor', vendor: 'DCA Internal', type: 'Automation', status: 'Active',
    desc: 'Safe, audited execution of pre-approved remediation playbooks with dry-run and rollback.',
    capabilities: ['Step-by-step execution', 'Health check gates', 'Rollback triggers', 'Full audit log'],
    usedBy: ['AIOps Agent', 'Incident Commander'], version: 'v2.1.0' },
  { id: 'itsm-connector', category: 'observability', name: 'ITSM Connector', vendor: 'ServiceNow', type: 'Integration', status: 'Degraded',
    desc: 'Bidirectional integration with ServiceNow and Jira Service Desk for automatic ticket creation, enrichment, and resolution.',
    capabilities: ['Auto ticket creation', 'State sync', 'SLA tracking', 'Assignment routing'],
    usedBy: ['AIOps Agent', 'SRE Agent', 'Incident Commander'], version: 'v3.0.8' },
  { id: 'reg-feed', category: 'compliance', name: 'Regulatory Feed Aggregator', vendor: 'DCA Internal', type: 'Data Feed', status: 'Active',
    desc: 'Aggregates regulatory updates from 40+ official sources across 12 jurisdictions including FCA, SEC, EBA, FDA, EMA.',
    capabilities: ['40+ official sources', 'Daily delta feeds', 'Change classification', 'Alert on high-impact changes'],
    usedBy: ['Regulatory AI Agent', 'Compliance Monitor'], version: 'v1.5.2' },
  { id: 'evidence-vault', category: 'compliance', name: 'Evidence Vault Connector', vendor: 'Multi', type: 'GRC Integration', status: 'Active',
    desc: 'Integrates with GRC platforms (ServiceNow GRC, Archer, MetricStream) for evidence storage and audit trail management.',
    capabilities: ['ServiceNow GRC', 'Archer', 'MetricStream', 'Custom evidence store'],
    usedBy: ['Regulatory AI Agent', 'Audit Trail Analyser'], version: 'v2.3.0' },
  { id: 'meddra-engine', category: 'compliance', name: 'MedDRA Coding Engine', vendor: 'DCA + MSSO', type: 'Medical Coding', status: 'Active',
    desc: 'Auto-codes adverse events to MedDRA preferred terms, LLTs, and SOCs with MSSO browser validation.',
    capabilities: ['MedDRA 26.1', 'Auto-coding with confidence score', 'Human review queue', 'Batch processing'],
    usedBy: ['Clinical Safety Agent'], version: 'v26.1.0' },
];

/* ─────────────── MODEL REGISTRY ─────────────── */
export interface IModel {
  id: string;
  provider: string;
  providerColor: string;
  name: string;
  type: 'Text' | 'Multimodal' | 'Reasoning' | 'Image' | 'Audio';
  modality: string[];
  contextWindow: string;
  released: string;
  status: string;
  tier: string;
  cost: string;
  latency: string;
  strengths: string[];
  usedByPlatform: boolean;
}

export const MODEL_TYPE_COLORS: Record<string, string> = {
  Text: '#2E7D32',
  Multimodal: '#1565C0',
  Reasoning: '#6A1B9A',
  Image: '#C62828',
  Audio: '#00695C',
};

export const AI_MODELS: IModel[] = [
  /* Anthropic */
  { id: 'claude-opus', provider: 'Anthropic', providerColor: '#8844CC', name: 'Claude Opus 4', type: 'Text', modality: ['Text', 'Code', 'Analysis'], contextWindow: '200K', released: '2025', status: 'Active', tier: 'Critical', cost: '$15 / MTok in · $75 / MTok out', latency: '1.8s', strengths: ['Complex reasoning', 'Long-context synthesis', 'Nuanced judgment', 'Architecture review'], usedByPlatform: true },
  { id: 'claude-sonnet', provider: 'Anthropic', providerColor: '#8844CC', name: 'Claude Sonnet 4', type: 'Text', modality: ['Text', 'Code', 'Analysis'], contextWindow: '200K', released: '2025', status: 'Active', tier: 'Standard', cost: '$3 / MTok in · $15 / MTok out', latency: '0.9s', strengths: ['Balanced speed & quality', 'Code generation', 'Structured output', 'Analysis'], usedByPlatform: true },
  { id: 'claude-haiku', provider: 'Anthropic', providerColor: '#8844CC', name: 'Claude Haiku 3.5', type: 'Text', modality: ['Text', 'Code'], contextWindow: '200K', released: '2024', status: 'Active', tier: 'High-volume', cost: '$0.25 / MTok in · $1.25 / MTok out', latency: '0.3s', strengths: ['Ultra-low latency', 'High throughput', 'Classification', 'Simple extraction'], usedByPlatform: true },
  /* OpenAI */
  { id: 'gpt4o', provider: 'OpenAI', providerColor: '#10A37F', name: 'GPT-4o', type: 'Multimodal', modality: ['Text', 'Image', 'Audio', 'Video'], contextWindow: '128K', released: '2024', status: 'Available', tier: 'Standard', cost: '$2.50 / MTok in · $10 / MTok out', latency: '1.1s', strengths: ['Vision understanding', 'Audio transcription', 'Multimodal reasoning', 'Broad capability'], usedByPlatform: false },
  { id: 'gpt4o-mini', provider: 'OpenAI', providerColor: '#10A37F', name: 'GPT-4o mini', type: 'Multimodal', modality: ['Text', 'Image'], contextWindow: '128K', released: '2024', status: 'Available', tier: 'High-volume', cost: '$0.15 / MTok in · $0.60 / MTok out', latency: '0.4s', strengths: ['Cost-efficient', 'Fast vision tasks', 'Function calling', 'Batch processing'], usedByPlatform: false },
  { id: 'o3', provider: 'OpenAI', providerColor: '#10A37F', name: 'o3', type: 'Reasoning', modality: ['Text', 'Code'], contextWindow: '200K', released: '2025', status: 'Available', tier: 'Critical', cost: '$10 / MTok in · $40 / MTok out', latency: '15–120s', strengths: ['Deep mathematical reasoning', 'Scientific research', 'Complex code', 'Step-by-step logic'], usedByPlatform: false },
  { id: 'o4-mini', provider: 'OpenAI', providerColor: '#10A37F', name: 'o4-mini', type: 'Reasoning', modality: ['Text', 'Code'], contextWindow: '200K', released: '2025', status: 'Available', tier: 'Standard', cost: '$1.1 / MTok in · $4.4 / MTok out', latency: '5–30s', strengths: ['Fast reasoning', 'STEM tasks', 'Cost-efficient chain-of-thought'], usedByPlatform: false },
  { id: 'dall-e-3', provider: 'OpenAI', providerColor: '#10A37F', name: 'DALL·E 3', type: 'Image', modality: ['Image Generation'], contextWindow: '—', released: '2023', status: 'Available', tier: 'Specialist', cost: '$0.04–0.12 / image', latency: '8–15s', strengths: ['Photorealistic generation', 'Creative illustration', 'Brand asset creation', 'Diagram generation'], usedByPlatform: false },
  { id: 'whisper', provider: 'OpenAI', providerColor: '#10A37F', name: 'Whisper v3', type: 'Audio', modality: ['Speech-to-Text'], contextWindow: '—', released: '2024', status: 'Available', tier: 'Specialist', cost: '$0.006 / min', latency: 'Real-time', strengths: ['Multilingual transcription', 'Noise robustness', 'Meeting intelligence', 'Medical dictation'], usedByPlatform: false },
  /* Google */
  { id: 'gemini-25-pro', provider: 'Google', providerColor: '#4285F4', name: 'Gemini 2.5 Pro', type: 'Multimodal', modality: ['Text', 'Image', 'Video', 'Audio', 'Code'], contextWindow: '1M', released: '2025', status: 'Available', tier: 'Critical', cost: '$1.25 / MTok in · $10 / MTok out', latency: '2.0s', strengths: ['1M context window', 'Video understanding', 'Long-document analysis', 'Multi-modal reasoning'], usedByPlatform: false },
  { id: 'gemini-25-flash', provider: 'Google', providerColor: '#4285F4', name: 'Gemini 2.5 Flash', type: 'Multimodal', modality: ['Text', 'Image', 'Code'], contextWindow: '1M', released: '2025', status: 'Available', tier: 'Standard', cost: '$0.15 / MTok in · $0.60 / MTok out', latency: '0.6s', strengths: ['Fast multimodal', 'Long context', 'Cost efficient', 'Document processing'], usedByPlatform: false },
  { id: 'imagen-3', provider: 'Google', providerColor: '#4285F4', name: 'Imagen 3', type: 'Image', modality: ['Image Generation'], contextWindow: '—', released: '2024', status: 'Available', tier: 'Specialist', cost: '$0.04 / image', latency: '5–10s', strengths: ['Photorealism', 'Text rendering in images', 'Style control', 'Brand safety filters'], usedByPlatform: false },
  /* Meta */
  { id: 'llama-3-405b', provider: 'Meta', providerColor: '#0064E0', name: 'Llama 3.1 405B', type: 'Text', modality: ['Text', 'Code'], contextWindow: '128K', released: '2024', status: 'Available', tier: 'Critical', cost: 'Self-hosted / API', latency: '1.5s', strengths: ['Open weights', 'On-premise deployment', 'Fine-tunable', 'No data residency concerns'], usedByPlatform: false },
  { id: 'llama-3-70b', provider: 'Meta', providerColor: '#0064E0', name: 'Llama 3.1 70B', type: 'Text', modality: ['Text', 'Code'], contextWindow: '128K', released: '2024', status: 'Available', tier: 'Standard', cost: 'Self-hosted / API', latency: '0.7s', strengths: ['Efficient open model', 'Fast inference', 'Broad task coverage', 'Private deployment'], usedByPlatform: false },
  { id: 'llama-3-11bv', provider: 'Meta', providerColor: '#0064E0', name: 'Llama 3.2 11B Vision', type: 'Multimodal', modality: ['Text', 'Image'], contextWindow: '128K', released: '2024', status: 'Available', tier: 'High-volume', cost: 'Self-hosted / API', latency: '0.3s', strengths: ['Lightweight vision model', 'Edge deployment', 'Low latency', 'On-device inference'], usedByPlatform: false },
  /* Mistral */
  { id: 'mistral-large', provider: 'Mistral AI', providerColor: '#FF6B35', name: 'Mistral Large 2', type: 'Text', modality: ['Text', 'Code'], contextWindow: '128K', released: '2024', status: 'Available', tier: 'Standard', cost: '$2 / MTok in · $6 / MTok out', latency: '0.8s', strengths: ['European data residency', 'Multilingual', 'Function calling', 'Code generation'], usedByPlatform: false },
  { id: 'mistral-small', provider: 'Mistral AI', providerColor: '#FF6B35', name: 'Mistral Small 3.1', type: 'Multimodal', modality: ['Text', 'Image', 'Code'], contextWindow: '128K', released: '2025', status: 'Available', tier: 'High-volume', cost: '$0.10 / MTok in · $0.30 / MTok out', latency: '0.3s', strengths: ['Ultra-low cost', 'Vision support', 'EU compliance', 'Fast classification'], usedByPlatform: false },
  /* AWS */
  { id: 'titan-text', provider: 'AWS', providerColor: '#FF9900', name: 'Amazon Titan Text G1', type: 'Text', modality: ['Text'], contextWindow: '8K', released: '2023', status: 'Available', tier: 'Standard', cost: '$0.0008–0.0016 / 1K tokens', latency: '0.5s', strengths: ['AWS-native integration', 'Bedrock deployment', 'Enterprise SLA', 'Data stays in AWS'], usedByPlatform: false },
  { id: 'nova-pro', provider: 'AWS', providerColor: '#FF9900', name: 'Amazon Nova Pro', type: 'Multimodal', modality: ['Text', 'Image', 'Video'], contextWindow: '300K', released: '2024', status: 'Available', tier: 'Standard', cost: '$0.80 / MTok in · $3.20 / MTok out', latency: '1.0s', strengths: ['Bedrock-native', 'Video understanding', 'Long context', 'AWS compliance'], usedByPlatform: false },
  /* Private */
  { id: 'custom-ft', provider: 'Private', providerColor: '#64748B', name: 'Client Fine-tuned Model', type: 'Text', modality: ['Text', 'Code'], contextWindow: '128K', released: '—', status: 'Active', tier: 'Domain-specific', cost: 'Self-hosted', latency: '1.1s', strengths: ['Domain fine-tuning', 'Data isolation', 'On-premise', 'Regulatory compliance'], usedByPlatform: true },
];

/* ─────────────── LLM HEALTH ─────────────── */
export interface IModelHealth {
  id: string;
  name: string;
  provider: string;
  providerColor: string;
  type: string;
  uptime: string;
  latency: string;
  reqToday: number;
  errRate: string;
  status: string;
  usedByPlatform: boolean;
}

const HEALTH_OVERRIDES: Record<string, Partial<IModelHealth>> = {
  'claude-opus':   { uptime: '99.4%', reqToday: 1240,  errRate: '0.18%' },
  'claude-sonnet': { uptime: '99.8%', reqToday: 5820,  errRate: '0.06%' },
  'claude-haiku':  { uptime: '99.9%', reqToday: 12600, errRate: '0.02%' },
  'gpt4o':         { uptime: '99.6%' },
  'gpt4o-mini':    { uptime: '99.7%' },
  'o3':            { uptime: '98.9%' },
  'o4-mini':       { uptime: '99.1%' },
  'dall-e-3':      { uptime: '99.2%' },
  'whisper':       { uptime: '99.5%' },
  'gemini-25-pro': { uptime: '99.3%' },
  'gemini-25-flash': { uptime: '99.6%' },
  'imagen-3':      { uptime: '99.0%' },
  'llama-3-405b':  { uptime: '99.1%' },
  'llama-3-70b':   { uptime: '99.4%' },
  'llama-3-11bv':  { uptime: '99.3%' },
  'mistral-large': { uptime: '99.2%' },
  'mistral-small': { uptime: '99.5%' },
  'titan-text':    { uptime: '99.7%' },
  'nova-pro':      { uptime: '99.4%' },
  'custom-ft':     { uptime: '98.9%', reqToday: 310, errRate: '0.42%' },
};

export const MODEL_HEALTH: IModelHealth[] = AI_MODELS.map((m) => ({
  id: m.id,
  name: m.name,
  provider: m.provider,
  providerColor: m.providerColor,
  type: m.type,
  latency: m.latency,
  uptime: HEALTH_OVERRIDES[m.id]?.uptime || '99.4%',
  reqToday: HEALTH_OVERRIDES[m.id]?.reqToday ?? 0,
  errRate: HEALTH_OVERRIDES[m.id]?.errRate || (m.usedByPlatform ? '0.10%' : '—'),
  status: m.usedByPlatform ? 'Operational' : 'Available',
  usedByPlatform: m.usedByPlatform,
}));

export interface IIntegration {
  name: string;
  status: 'Healthy' | 'Degraded';
  latency: string;
  provider: string;
}

export const INTEGRATIONS: IIntegration[] = [
  { name: 'Anthropic API', status: 'Healthy', latency: '420ms', provider: 'Anthropic' },
  { name: 'OpenAI API', status: 'Healthy', latency: '310ms', provider: 'OpenAI' },
  { name: 'Google AI Studio', status: 'Healthy', latency: '280ms', provider: 'Google' },
  { name: 'AWS Bedrock', status: 'Healthy', latency: '210ms', provider: 'AWS' },
  { name: 'Azure OpenAI', status: 'Healthy', latency: '180ms', provider: 'OpenAI' },
  { name: 'Mistral API', status: 'Healthy', latency: '195ms', provider: 'Mistral AI' },
  { name: 'Meta Llama API', status: 'Healthy', latency: '245ms', provider: 'Meta' },
  { name: 'Private Endpoint', status: 'Healthy', latency: '95ms', provider: 'Private' },
  { name: 'Vector DB', status: 'Healthy', latency: '12ms', provider: 'Internal' },
  { name: 'DCA RAG Store', status: 'Healthy', latency: '34ms', provider: 'Internal' },
  { name: 'ServiceNow', status: 'Degraded', latency: '1.2s', provider: 'Integration' },
  { name: 'Datadog', status: 'Healthy', latency: '88ms', provider: 'Integration' },
];

/* ─────────────── LLM SETTINGS ─────────────── */
export interface ILlmToggleModel {
  id: string;
  name: string;
  provider: string;
  color: string;
  tier: string;
  latency: string;
  cost: string;
  strengths: string[];
  defaultEnabled: boolean;
  defaultBudget: number;
}

export const LLM_SETTINGS_MODELS: ILlmToggleModel[] = [
  { id: 'claude-opus',     name: 'Claude Opus 4',      provider: 'Anthropic',  color: '#8844CC', tier: 'Critical',        latency: '1.8s',    cost: '$15 / MTok',   strengths: ['Complex reasoning', 'Long-context synthesis', 'Architecture review'], defaultEnabled: true,  defaultBudget: 500 },
  { id: 'claude-sonnet',   name: 'Claude Sonnet 4',    provider: 'Anthropic',  color: '#6AAF1F', tier: 'Standard',        latency: '0.9s',    cost: '$3 / MTok',    strengths: ['Balanced speed & quality', 'Code generation', 'Structured outputs'], defaultEnabled: true,  defaultBudget: 300 },
  { id: 'claude-haiku',    name: 'Claude Haiku 3.5',   provider: 'Anthropic',  color: '#00B5AD', tier: 'High-volume',     latency: '0.3s',    cost: '$0.25 / MTok', strengths: ['Ultra-low latency', 'High throughput', 'Classification'], defaultEnabled: true,  defaultBudget: 100 },
  { id: 'gpt4o',           name: 'GPT-4o',             provider: 'OpenAI',     color: '#10A37F', tier: 'Standard',        latency: '1.1s',    cost: '$2.50 / MTok', strengths: ['Vision understanding', 'Audio transcription', 'Multimodal'], defaultEnabled: false, defaultBudget: 200 },
  { id: 'o3',              name: 'o3',                 provider: 'OpenAI',     color: '#10A37F', tier: 'Critical',        latency: '15–120s', cost: '$10 / MTok',   strengths: ['Deep math reasoning', 'Scientific research', 'Complex code'], defaultEnabled: false, defaultBudget: 150 },
  { id: 'gemini-25-pro',   name: 'Gemini 2.5 Pro',     provider: 'Google',     color: '#4285F4', tier: 'Critical',        latency: '2.0s',    cost: '$1.25 / MTok', strengths: ['1M context', 'Video understanding', 'Long-document'], defaultEnabled: false, defaultBudget: 200 },
  { id: 'gemini-25-flash', name: 'Gemini 2.5 Flash',   provider: 'Google',     color: '#4285F4', tier: 'Standard',        latency: '0.6s',    cost: '$0.15 / MTok', strengths: ['Fast multimodal', 'Long context', 'Cost efficient'], defaultEnabled: false, defaultBudget: 120 },
  { id: 'llama-3-405b',    name: 'Llama 3.1 405B',     provider: 'Meta',       color: '#0064E0', tier: 'Critical',        latency: '1.5s',    cost: 'Self-hosted',  strengths: ['Open weights', 'On-premise', 'Fine-tunable'], defaultEnabled: false, defaultBudget: 100 },
  { id: 'mistral-large',   name: 'Mistral Large 2',    provider: 'Mistral AI', color: '#FF6B35', tier: 'Standard',        latency: '0.8s',    cost: '$2 / MTok',    strengths: ['EU data residency', 'Multilingual', 'Function calling'], defaultEnabled: false, defaultBudget: 150 },
  { id: 'nova-pro',        name: 'Amazon Nova Pro',    provider: 'AWS',        color: '#FF9900', tier: 'Standard',        latency: '1.0s',    cost: '$0.80 / MTok', strengths: ['Bedrock-native', 'Video understanding', 'AWS compliance'], defaultEnabled: false, defaultBudget: 150 },
  { id: 'custom-ft',       name: 'Client Fine-tuned',  provider: 'Private',    color: '#64748B', tier: 'Domain-specific', latency: '1.1s',    cost: 'Self-hosted',  strengths: ['Domain fine-tuning', 'Data isolation', 'On-premise'], defaultEnabled: false, defaultBudget: 200 },
];

export const ROUTING_RULES = [
  { tier: 'Critical / multi-step reasoning', model: 'Claude Opus 4 · GPT o3',                 color: '#8844CC', tokens: '>4K output or complex chain' },
  { tier: 'Standard operations',             model: 'Claude Sonnet · GPT-4o · Gemini Flash',  color: '#6AAF1F', tokens: 'Standard tasks, balanced' },
  { tier: 'High-volume / lightweight',       model: 'Claude Haiku · Mistral Small',           color: '#00B5AD', tokens: '<1K output, classification' },
  { tier: 'Long-context (>200K)',            model: 'Gemini 2.5 Pro',                          color: '#4285F4', tokens: 'Long documents and video' },
  { tier: 'Private / sensitive data',        model: 'Client Fine-tuned · Llama 3.1',          color: '#1E5FA8', tokens: 'Client data isolation required' },
];

export const FALLBACK_SETTINGS: { label: string; value: string; action: string }[] = [
  { label: 'Auto-fallback on rate limit',  value: 'Enabled',       action: 'Toggle' },
  { label: 'Retry on timeout',             value: '3 retries',     action: 'Configure' },
  { label: 'Max token budget per request', value: '8,192 tokens',  action: 'Configure' },
  { label: 'PII detection & redaction',    value: 'Enforced',      action: 'Configure' },
  { label: 'Audit logging',                value: 'Full trace',    action: 'View Logs' },
  { label: 'Human-in-the-loop threshold',  value: '> $500 tasks',  action: 'Configure' },
];

export const ROUTING_MODES: { id: string; label: string; desc: string }[] = [
  { id: 'auto',       label: 'Auto (recommended)', desc: 'Engine chooses the optimal model per task' },
  { id: 'costfirst',  label: 'Cost-first',         desc: 'Prefer cheaper models, escalate on failure' },
  { id: 'speedfirst', label: 'Speed-first',        desc: 'Prefer fastest models, fallback as needed' },
];

/* ─────────────── SUMMARY (Observability + FinOps) ─────────────── */
export const SUMMARY_LABELS = {
  title: 'AI Accelerated Summary',
  subtitle:
    'A single observability lens across toolkits, skills, models, guardrails — with FinOps and runtime telemetry.',
  asOf: 'Last 24 hours · auto-refresh 60s',
};

export interface ISummaryKpi {
  label: string;
  value: string;
  delta: string;
  trend: 'up' | 'down' | 'flat';
  color: string;
  desc: string;
}

export const SUMMARY_KPIS: ISummaryKpi[] = [
  { label: 'Total Spend (24h)', value: '$1,842', delta: '+6.4%', trend: 'up',   color: '#1E5FA8', desc: 'Across all providers' },
  { label: 'Tokens Processed',  value: '48.7M',  delta: '+11.2%',trend: 'up',   color: '#8844CC', desc: 'In + Out, all models' },
  { label: 'Avg Latency (P50)', value: '0.94s',  delta: '-3.1%', trend: 'down', color: '#00B5AD', desc: 'Cross-model weighted' },
  { label: 'Guardrail Compliance', value: '99.62%', delta: '+0.08%', trend: 'up', color: '#6AAF1F', desc: '7 pillars, enforced' },
  { label: 'Error Rate',        value: '0.21%',  delta: '-0.04%',trend: 'down', color: '#16a34a', desc: 'Failed / total requests' },
  { label: 'Active Surfaces',   value: '8 / 44 / 18 / 4', delta: 'agents/skills/toolkits/models', trend: 'flat', color: '#6E5BD9', desc: 'Live registry counts' },
];

/* FinOps — cost & token usage per model (24h) */
export interface IModelUsage {
  id: string;
  name: string;
  provider: string;
  color: string;
  tokensIn: number;     // millions
  tokensOut: number;    // millions
  cost: number;         // USD
  requests: number;
  budget: number;       // USD daily
}

export const MODEL_USAGE: IModelUsage[] = [
  { id: 'claude-opus',     name: 'Claude Opus 4',     provider: 'Anthropic', color: '#8844CC', tokensIn: 4.2,  tokensOut: 1.1,  cost: 145.5,  requests: 1240,  budget: 500 },
  { id: 'claude-sonnet',   name: 'Claude Sonnet 4',   provider: 'Anthropic', color: '#6AAF1F', tokensIn: 18.4, tokensOut: 5.6,  cost: 139.2,  requests: 5820,  budget: 300 },
  { id: 'claude-haiku',    name: 'Claude Haiku 3.5',  provider: 'Anthropic', color: '#00B5AD', tokensIn: 11.2, tokensOut: 3.1,  cost: 6.7,    requests: 12600, budget: 100 },
  { id: 'gpt4o',           name: 'GPT-4o',            provider: 'OpenAI',    color: '#10A37F', tokensIn: 2.6,  tokensOut: 0.7,  cost: 13.5,   requests: 980,   budget: 200 },
  { id: 'gemini-25-flash', name: 'Gemini 2.5 Flash',  provider: 'Google',    color: '#4285F4', tokensIn: 1.4,  tokensOut: 0.4,  cost: 0.45,   requests: 720,   budget: 120 },
  { id: 'custom-ft',       name: 'Client Fine-tuned', provider: 'Private',   color: '#64748B', tokensIn: 0.6,  tokensOut: 0.15, cost: 0,      requests: 310,   budget: 200 },
];

/* FinOps — cost roll-up per offering / business surface */
export interface ICostBreakdown {
  surface: string;
  spend: number;     // USD 24h
  pct: number;       // % of total
  color: string;
}

export const COST_BY_OFFERING: ICostBreakdown[] = [
  { surface: 'Cloud Enablement',   spend: 812, pct: 44, color: '#6AAF1F' },
  { surface: 'Industry Solutions', spend: 545, pct: 30, color: '#1E5FA8' },
  { surface: 'Platform-wide',      spend: 318, pct: 17, color: '#8844CC' },
  { surface: 'R&D / Experiments',  spend: 167, pct: 9,  color: '#00B5AD' },
];

export const TOP_COST_SKILLS: { skill: string; cost: number; tokens: string; color: string }[] = [
  { skill: 'Code Review Companion',    cost: 184, tokens: '6.1M', color: '#2E7D32' },
  { skill: 'Terraform Developer',      cost: 162, tokens: '5.4M', color: '#4527A0' },
  { skill: 'AIOps Incident Correlator',cost: 141, tokens: '4.9M', color: '#6A1B9A' },
  { skill: 'Clinical Narrative Writer',cost: 118, tokens: '3.8M', color: '#388E3C' },
  { skill: 'Enterprise Knowledge Q&A', cost:  96, tokens: '3.4M', color: '#8844CC' },
];

/* Sparkline — last 7 days cost (USD) */
export const COST_TREND_7D: { day: string; cost: number }[] = [
  { day: 'Mon', cost: 1490 },
  { day: 'Tue', cost: 1610 },
  { day: 'Wed', cost: 1580 },
  { day: 'Thu', cost: 1735 },
  { day: 'Fri', cost: 1812 },
  { day: 'Sat', cost: 1240 },
  { day: 'Sun', cost: 1842 },
];

/* Observability — latency percentiles per model */
export interface ILatencyRow {
  id: string;
  name: string;
  provider: string;
  color: string;
  p50: string;
  p95: string;
  p99: string;
  errRate: string;
  rps: number;        // requests / sec
  status: 'Healthy' | 'Degraded' | 'Watch';
}

export const LATENCY_TABLE: ILatencyRow[] = [
  { id: 'claude-opus',     name: 'Claude Opus 4',    provider: 'Anthropic', color: '#8844CC', p50: '1.8s',  p95: '3.4s',  p99: '5.1s',  errRate: '0.18%', rps: 0.9,  status: 'Healthy'  },
  { id: 'claude-sonnet',   name: 'Claude Sonnet 4',  provider: 'Anthropic', color: '#6AAF1F', p50: '0.9s',  p95: '1.7s',  p99: '2.3s',  errRate: '0.06%', rps: 4.2,  status: 'Healthy'  },
  { id: 'claude-haiku',    name: 'Claude Haiku 3.5', provider: 'Anthropic', color: '#00B5AD', p50: '0.3s',  p95: '0.6s',  p99: '0.9s',  errRate: '0.02%', rps: 9.1,  status: 'Healthy'  },
  { id: 'gpt4o',           name: 'GPT-4o',           provider: 'OpenAI',    color: '#10A37F', p50: '1.1s',  p95: '2.0s',  p99: '2.8s',  errRate: '0.12%', rps: 0.7,  status: 'Healthy'  },
  { id: 'gemini-25-flash', name: 'Gemini 2.5 Flash', provider: 'Google',    color: '#4285F4', p50: '0.6s',  p95: '1.1s',  p99: '1.6s',  errRate: '0.09%', rps: 0.5,  status: 'Watch'    },
  { id: 'custom-ft',       name: 'Client FT',        provider: 'Private',   color: '#64748B', p50: '1.1s',  p95: '2.4s',  p99: '3.6s',  errRate: '0.42%', rps: 0.2,  status: 'Degraded' },
];

/* Observability — toolkit health (mirrors AI_TOOLKITS but adds runtime perf) */
export interface IToolkitObservability {
  id: string;
  name: string;
  category: string;
  status: 'Active' | 'Degraded';
  p95: string;
  successRate: string;
  callsToday: number;
}

export const TOOLKIT_OBSERVABILITY: IToolkitObservability[] = [
  { id: 'rag-engine',        name: 'DCA RAG Engine',          category: 'rag',           status: 'Active',   p95: '180ms', successRate: '99.92%', callsToday: 14820 },
  { id: 'vector-db',         name: 'Vector Database',         category: 'rag',           status: 'Active',   p95: '22ms',  successRate: '99.99%', callsToday: 28400 },
  { id: 'handoff-bus',       name: 'Agent Handoff Bus',       category: 'orchestration', status: 'Active',   p95: '14ms',  successRate: '99.98%', callsToday: 9120  },
  { id: 'iac-runner',        name: 'IaC Execution Runner',    category: 'devops',        status: 'Active',   p95: '2.4s',  successRate: '99.10%', callsToday: 412   },
  { id: 'alert-correlator',  name: 'Alert Correlation Engine',category: 'observability', status: 'Active',   p95: '320ms', successRate: '99.75%', callsToday: 6210  },
  { id: 'itsm-connector',    name: 'ITSM Connector',          category: 'observability', status: 'Degraded', p95: '1.2s',  successRate: '96.40%', callsToday: 482   },
  { id: 'reg-feed',          name: 'Regulatory Feed',         category: 'compliance',    status: 'Active',   p95: '780ms', successRate: '99.50%', callsToday: 96    },
];

/* Guardrail compliance snapshot per pillar */
export interface IGuardrailSnapshot {
  pillarId: string;
  pillar: string;
  color: string;
  enforced: number;       // % of evals enforced (blocked + allowed)
  violations24h: number;  // number of blocked outputs
  trend: 'up' | 'down' | 'flat';
}

export const GUARDRAIL_SNAPSHOT: IGuardrailSnapshot[] = [
  { pillarId: 'safe-secure',              pillar: 'Safe & Secure',              color: '#0f172a', enforced: 100,   violations24h: 12, trend: 'down' },
  { pillarId: 'private',                  pillar: 'Private',                    color: '#1d4ed8', enforced: 100,   violations24h: 4,  trend: 'flat' },
  { pillarId: 'transparent-explainable',  pillar: 'Transparent & Explainable',  color: '#0d9488', enforced: 99.8,  violations24h: 9,  trend: 'down' },
  { pillarId: 'fair-impartial',           pillar: 'Fair & Impartial',           color: '#16a34a', enforced: 99.6,  violations24h: 7,  trend: 'down' },
  { pillarId: 'responsible',              pillar: 'Responsible',                color: '#65a30d', enforced: 99.9,  violations24h: 2,  trend: 'flat' },
  { pillarId: 'accountable',              pillar: 'Accountable',                color: '#84cc16', enforced: 100,   violations24h: 0,  trend: 'flat' },
  { pillarId: 'robust-reliable',          pillar: 'Robust & Reliable',          color: '#064e3b', enforced: 99.4,  violations24h: 18, trend: 'up'   },
];

/* Live incident / signal feed for the summary view */
export interface ISignalEvent {
  id: string;
  ts: string;
  severity: 'info' | 'warn' | 'crit';
  source: string;
  message: string;
}

export const SIGNAL_FEED: ISignalEvent[] = [
  { id: 's1', ts: '12:42', severity: 'warn', source: 'ITSM Connector',     message: 'P95 latency above 1s for 6 min — auto-fallback engaged.' },
  { id: 's2', ts: '12:31', severity: 'info', source: 'Robust & Reliable',  message: 'Guardrail re-baselined: drift below 0.5%.' },
  { id: 's3', ts: '12:18', severity: 'crit', source: 'Client Fine-tuned',  message: 'Error rate 0.42% breached SLO (0.30%). On-call paged.' },
  { id: 's4', ts: '11:55', severity: 'info', source: 'FinOps',             message: 'Sonnet 4 spend at 46% of daily budget — on track.' },
  { id: 's5', ts: '11:40', severity: 'warn', source: 'Safe & Secure',      message: '12 prompt-injection attempts blocked in last hour.' },
];
